// MARK://dhk8/admin-shell/pages/modules/InventoryMovementsPage
// Historial de movimientos de stock → dhk8-erp-inventory (en prod).
// Tipos: RECEIPT / CONSUMPTION / TRANSFER / ADJUSTMENT / MERMA.
//
// DS RBAC (Gustavo 2026-06-22): la lista usa DataTable + FilterBar (búsqueda por
// insumo/ref + filtro por tipo + filtro por fecha). El cliente solo expone un
// movimiento que se pueda CREAR a mano: el ajuste (adjustStock, que admite `reason`,
// así un ajuste negativo con motivo "Merma" cubre la merma manual). Recepciones y
// consumos llegan de órdenes de compra / ventas — no se crean desde aquí. Por eso el
// botón es "Registrar ajuste" (fija la existencia a N; delta = nuevo − actual): no se
// fabrica un endpoint de recepción/merma que el subgrafo no expone. Ver reporte.
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, SlidersHorizontal, TriangleAlert, ChevronLeft, Plus } from 'lucide-react'
import * as inv from '../../lib/clients/inventory.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import { useToast } from '../../components/Toast.js'
import { unitShort } from '../../components/UnitPicker.js'

interface TypeMeta { label: string; tone: StatusTone; Icon: typeof ArrowDownToLine }
const TYPE_META: Record<string, TypeMeta> = {
  RECEIPT: { label: 'Entrada', tone: 'ok', Icon: ArrowDownToLine },
  CONSUMPTION: { label: 'Consumo', tone: 'info', Icon: ArrowUpFromLine },
  TRANSFER: { label: 'Traspaso', tone: 'info', Icon: ArrowLeftRight },
  ADJUSTMENT: { label: 'Ajuste', tone: 'warn', Icon: SlidersHorizontal },
  MERMA: { label: 'Merma', tone: 'danger', Icon: TriangleAlert },
}
function metaFor(type: string): TypeMeta {
  return TYPE_META[type.toUpperCase()] ?? { label: type, tone: 'info', Icon: SlidersHorizontal }
}

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'RECEIPT', label: 'Entradas' },
  { value: 'CONSUMPTION', label: 'Consumo' },
  { value: 'TRANSFER', label: 'Traspasos' },
  { value: 'ADJUSTMENT', label: 'Ajustes' },
  { value: 'MERMA', label: 'Merma' },
]

// Rango de fechas relativo, calculado en cliente sobre createdAt.
const DAY = 86400000
const DATE_OPTIONS = [
  { value: '', label: 'Cualquier fecha' },
  { value: '1', label: 'Hoy' },
  { value: '7', label: 'Últimos 7 días' },
  { value: '30', label: 'Últimos 30 días' },
]

function fmtDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export default function InventoryMovementsPage() {
  const { toast } = useToast()
  const list = useAsync((s) => inv.listMovements(s))
  const items = useAsync((s) => inv.listInventory(s))

  const [typeFilter, setTypeFilter] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState | undefined>({ key: 'createdAt', dir: 'desc' })

  // Registrar ajuste a mano (único movimiento creable por el contrato: adjustStock).
  const [adjusting, setAdjusting] = useState(false)
  const [adjForm, setAdjForm] = useState<{ stockItemId: string; newQty: number; reason: string }>({ stockItemId: '', newQty: 0, reason: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const itemName = (id: string) => items.data?.find((i) => i.id === id)?.name ?? '(insumo no encontrado)'
  const selectedItem = items.data?.find((i) => i.id === adjForm.stockItemId)

  function openAdjust() {
    const first = items.data?.[0]
    setAdjForm({ stockItemId: first?.id ?? '', newQty: first?.onHand ?? 0, reason: '' })
    setError(null); setAdjusting(true)
  }
  function pickItem(stockItemId: string) {
    const it = items.data?.find((i) => i.id === stockItemId)
    setAdjForm((f) => ({ ...f, stockItemId, newQty: it?.onHand ?? 0 }))
  }
  async function saveAdjust() {
    setError(null)
    if (!adjForm.stockItemId) { setError('Selecciona un insumo.'); return }
    const current = selectedItem?.onHand ?? 0
    const delta = Math.round((adjForm.newQty - current) * 100) / 100
    if (delta === 0) { setError('La nueva existencia es igual a la actual.'); return }
    setSaving(true)
    try {
      await inv.adjustStock(adjForm.stockItemId, delta, adjForm.reason.trim() || null)
      setAdjusting(false); list.reload(); items.reload(); toast('Ajuste registrado', 'success')
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo registrar el ajuste.') }
    finally { setSaving(false) }
  }

  const rows = useMemo(() => {
    let r = list.data ?? []
    if (typeFilter) r = r.filter((m) => m.type.toUpperCase() === typeFilter)
    if (dateFilter) {
      const since = Date.now() - Number(dateFilter) * DAY
      r = r.filter((m) => new Date(m.createdAt).getTime() >= since)
    }
    const q = search.trim().toLowerCase()
    if (q) r = r.filter((m) => itemName(m.stockItemId).toLowerCase().includes(q) || (m.ref ?? '').toLowerCase().includes(q) || (m.reason ?? '').toLowerCase().includes(q))
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      r = r.slice().sort((a, b) => {
        let av: number | string, bv: number | string
        switch (sort.key) {
          case 'createdAt': av = a.createdAt; bv = b.createdAt; break
          case 'item': av = itemName(a.stockItemId).toLowerCase(); bv = itemName(b.stockItemId).toLowerCase(); break
          case 'type': av = a.type.toLowerCase(); bv = b.type.toLowerCase(); break
          case 'qty': av = a.qty; bv = b.qty; break
          default: return 0
        }
        return av < bv ? -dir : av > bv ? dir : 0
      })
    }
    return r
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, typeFilter, dateFilter, search, sort, items.data])

  const columns: DataTableColumn<inv.StockMovement>[] = [
    { key: 'createdAt', header: 'Fecha', sortable: true, render: (m) => <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDate(m.createdAt)}</span> },
    {
      key: 'item', header: 'Insumo', sortable: true, render: (m) => {
        const meta = metaFor(m.type)
        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 'var(--pos-weight-medium, 500)' }}><meta.Icon size={14} aria-hidden style={{ opacity: 0.65 }} />{itemName(m.stockItemId)}</span>
      },
    },
    { key: 'type', header: 'Tipo', sortable: true, render: (m) => { const meta = metaFor(m.type); return <StatusPill tone={meta.tone} label={meta.label} /> } },
    { key: 'detail', header: 'Detalle', render: (m) => <span style={{ color: 'var(--pos-fg-secondary)' }}>{[m.reason, m.ref ? `ref ${m.ref}` : null, m.reversed ? 'revertido' : null].filter(Boolean).join(' · ') || '—'}</span> },
    {
      key: 'qty', header: 'Cantidad', align: 'right', sortable: true, render: (m) => {
        const positive = m.qty >= 0
        return <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: positive ? 'var(--pos-success)' : 'var(--pos-danger)' }}>{positive ? '+' : ''}{m.qty}</span>
      },
    },
  ]

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link className="adm-btn adm-btn--ghost adm-btn--sm" to="/inventory" aria-label="Volver a inventario"><ChevronLeft size={18} /></Link>
          <div>
            <h1 className="adm-page__title">Movimientos de inventario</h1>
            <p className="adm-page__sub">Historial de entradas, consumo, traspasos, ajustes y merma</p>
          </div>
        </div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={openAdjust} disabled={!items.data || items.data.length === 0} title={items.data && items.data.length > 0 ? '' : 'No hay insumos que ajustar'}>
            <Plus size={16} /> Registrar ajuste
          </button>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && (
        <DataTable<inv.StockMovement>
          columns={columns}
          rows={rows}
          getRowKey={(m) => m.id}
          zebra
          sort={sort ?? null}
          onSortChange={setSort}
          emptyText={list.data.length === 0 ? 'Aún no hay movimientos registrados. Las recepciones de compra y las ventas aparecerán aquí.' : 'Sin movimientos para este filtro.'}
          toolbar={<FilterBar
            search={search}
            onSearch={setSearch}
            searchPlaceholder="Buscar insumo, motivo o ref…"
            filters={[
              { key: 'type', label: 'Tipo', value: typeFilter, onChange: setTypeFilter, options: TYPE_OPTIONS },
              { key: 'date', label: 'Fecha', value: dateFilter, onChange: setDateFilter, options: DATE_OPTIONS },
            ]}
          />}
        />
      )}

      {/* Registrar ajuste a mano (fija existencia a N; delta = nuevo − actual) */}
      <SectionedSheet open={adjusting} title="Registrar ajuste" onClose={() => { setAdjusting(false); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAdjusting(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveAdjust} disabled={saving || !adjForm.stockItemId}>{saving ? 'Registrando…' : 'Registrar'}</button>
        </>}>
        {error && <div className="adm-banner adm-banner--danger" role="alert">{error}</div>}
        <FormSection title="Insumo">
          <label className="adm-field"><span className="adm-field__label">Insumo</span>
            <select className="adm-select" value={adjForm.stockItemId} onChange={(e) => pickItem(e.target.value)} aria-label="Insumo">
              <option value="">— Selecciona insumo —</option>
              {(items.data ?? []).map((it) => <option key={it.id} value={it.id}>{it.name} ({it.onHand} {unitShort(it.unit)})</option>)}
            </select>
          </label>
        </FormSection>
        <FormSection title="Ajuste">
          {selectedItem && <p className="adm-row__meta" style={{ marginBottom: 0 }}>Existencia actual <span className="adm-mono">{selectedItem.onHand} {unitShort(selectedItem.unit)}</span></p>}
          <label className="adm-field"><span className="adm-field__label">Fijar existencia a{selectedItem ? ` (${unitShort(selectedItem.unit)})` : ''}</span><input className="adm-input" type="number" min={0} value={adjForm.newQty} onChange={(e) => setAdjForm({ ...adjForm, newQty: Number(e.target.value) })} /></label>
          <label className="adm-field"><span className="adm-field__label">Motivo (opcional · ej. “Merma”, “Conteo físico”)</span><input className="adm-input" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="Merma, conteo físico…" /></label>
          {selectedItem && <p className="adm-row__meta">Se registrará un movimiento de ajuste de <span className="adm-mono">{Math.round((adjForm.newQty - selectedItem.onHand) * 100) / 100} {unitShort(selectedItem.unit)}</span> (nuevo − actual).</p>}
        </FormSection>
      </SectionedSheet>
    </div>
  )
}
